import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Ambulance, HeartPulse, Baby, Users, Microscope, Syringe, CheckCircle2, ArrowUpRight,
  Building2, Stethoscope, Brain, MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useSettings, assetUrl } from "@/context/SettingsContext";

const iconMap = { Ambulance, HeartPulse, Baby, Users, Microscope, Syringe, Building2, Stethoscope, Brain };

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function Placements() {
  const { settings } = useSettings();
  const p = settings?.placements || {};
  const stats = settings?.stats || [];
  const exposure = p.exposureItems || [];
  const support = p.supportItems || [];

  return (
    <div data-testid="placements-page">
      <section className="py-20 lg:py-24 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div {...fadeUp} className="lg:col-span-7">
            <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-3">{p.eyebrow}</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05] tracking-tight">{p.heading}</h1>
            <p className="text-slate-600 text-lg mt-6 leading-relaxed max-w-2xl">{p.intro}</p>
          </motion.div>
          {p.image && (
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="lg:col-span-5">
              <div className="rounded-3xl overflow-hidden shadow-[0_30px_60px_-20px_rgba(15,23,42,0.25)]">
                <img src={assetUrl(p.image)} alt="Clinical training" className="w-full h-[360px] object-cover" />
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {exposure.length > 0 && (
        <section className="py-20 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mb-14">
              <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-3">Training Exposure</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">Where our students train</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {exposure.map((c, i) => {
                const Icon = iconMap[c.icon] || HeartPulse;
                return (
                  <motion.div
                    key={c.title}
                    {...fadeUp}
                    transition={{ ...fadeUp.transition, delay: i * 0.05 }}
                    data-testid={`exposure-card-${i}`}
                    className="bg-white rounded-2xl p-6 sm:p-7 border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.03)] hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all flex items-start gap-4"
                  >
                    <div className="w-12 h-12 rounded-xl grid place-items-center shrink-0" style={{ backgroundColor: "rgba(244,63,94,0.08)", color: "var(--brand-secondary)" }}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg text-slate-900 leading-snug">{c.title}</h3>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Training Hospitals - detailed cards */}
      {(p.hospitals || []).length > 0 && (
        <section className="py-20 lg:py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mb-14">
              <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-3">Training Partners</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">
                {p.hospitalsHeading || "Our Clinical Training Partners"}
              </h2>
              {p.hospitalsIntro && <p className="text-slate-600 mt-4 text-lg">{p.hospitalsIntro}</p>}
            </div>
            <div className="space-y-8">
              {(p.hospitals || []).map((h, i) => {
                const Icon = iconMap[h.icon] || Building2;
                const reverse = i % 2 === 1;
                return (
                  <motion.article
                    key={h.name}
                    {...fadeUp}
                    transition={{ ...fadeUp.transition, delay: i * 0.05 }}
                    data-testid={`hospital-card-${i}`}
                    className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-center bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] overflow-hidden"
                  >
                    {h.image && (
                      <div className={`lg:col-span-5 h-64 lg:h-full ${reverse ? "lg:order-2" : ""}`}>
                        <img src={assetUrl(h.image)} alt={h.name} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className={`lg:col-span-7 p-8 sm:p-10 ${reverse ? "lg:order-1" : ""}`}>
                      <div className="flex items-start justify-between gap-3 mb-4">
                        <Badge variant="secondary" className="rounded-full px-3 py-1 text-[11px] tracking-wider uppercase font-semibold bg-rose-50 text-rose-600 hover:bg-rose-50">
                          {h.tag}
                        </Badge>
                        <div className="w-11 h-11 rounded-xl grid place-items-center" style={{ backgroundColor: "rgba(30,58,138,0.08)", color: "var(--brand-primary)" }}>
                          <Icon className="w-5 h-5" />
                        </div>
                      </div>
                      <h3 className="font-heading text-2xl sm:text-3xl text-slate-900 leading-tight">{h.name}</h3>
                      <div className="mt-2 flex items-center gap-2 text-sm text-slate-500">
                        <MapPin className="w-4 h-4" /> {h.location}
                      </div>
                      <p className="mt-4 text-slate-600 leading-relaxed">{h.desc}</p>
                      {h.skills && h.skills.length > 0 && (
                        <div className="mt-5">
                          <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-3">Key Learning Areas</p>
                          <ul className="flex flex-wrap gap-2">
                            {h.skills.map((s) => (
                              <li key={s} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-700 text-xs font-medium px-3 py-1.5">
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> {s}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </motion.article>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-start">
          <motion.div {...fadeUp} className="lg:col-span-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-3">Career Guidance</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">
              Placement <span className="italic">Support</span>
            </h2>
            <p className="text-slate-600 text-lg mt-5 leading-relaxed">
              Our placement and career guidance initiatives help students prepare for professional success. Alumni have
              successfully pursued opportunities in reputed healthcare organizations across India and abroad.
            </p>
            <Link to="/contact#inquiry" className="mt-8 inline-block" data-testid="placement-apply-btn">
              <Button style={{ backgroundColor: "var(--brand-primary)" }} className="rounded-full h-12 px-7 text-white hover:-translate-y-0.5 hover:brightness-110 transition-all">
                Enroll for the next batch <ArrowUpRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>

          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="lg:col-span-7">
            <div className="bg-white rounded-3xl p-8 sm:p-10 border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
              <ul className="space-y-4">
                {support.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 grid place-items-center shrink-0 mt-0.5">
                      <CheckCircle2 className="w-4 h-4" />
                    </span>
                    <span className="text-slate-800 text-lg">{item}</span>
                  </li>
                ))}
              </ul>
              {stats.length > 0 && (
                <div className="mt-10 pt-8 border-t border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-6">
                  {stats.map((s) => (
                    <div key={s.label}>
                      <div className="font-heading text-3xl number-block" style={{ color: "var(--brand-primary)" }}>{s.value}</div>
                      <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{s.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
